import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Input, Select } from '@/components/ui/Field'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/format'
import { lineAmount, lineTotals, type EditableLine } from '@/lib/lineItems'

/**
 * The items on a quote or invoice, laid out like Pixieset's (29 Oct 2026):
 * each line has a name and a description, a quantity and a unit price, and its
 * amount is the two multiplied. Travel is quantity 100 (km) at R7. Under the
 * lines: Add item, Add discount, and quick adds from her collections and
 * add-ons; then subtotal, VAT when registered, and the total.
 */

export interface QuickAdd {
  id: string
  label: string
  unitPrice: number
  detail: string | null
  source: EditableLine['source']
}

let counter = 0
export const newLineKey = () => `line-${Date.now()}-${(counter += 1)}`

export function blankLine(patch: Partial<EditableLine> = {}): EditableLine {
  return { key: newLineKey(), description: '', detail: '', quantity: 1, unitPrice: 0, source: 'manual', sourceId: null, ...patch }
}

export function LineItemsEditor({
  lines,
  onChange,
  readOnly = false,
  vatRate,
  quickAdds = [],
  travel,
}: {
  lines: EditableLine[]
  onChange: (lines: EditableLine[]) => void
  readOnly?: boolean
  vatRate: number | null
  quickAdds?: QuickAdd[]
  /** The travel rate per km, and the project's return-trip distance if known. */
  travel?: { ratePerKm: number; km: number | null }
}) {
  const totals = lineTotals(lines, vatRate)
  const set = (key: string, patch: Partial<EditableLine>) =>
    onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)))
  const move = (index: number, by: -1 | 1) => {
    const next = [...lines]
    const [item] = next.splice(index, 1)
    if (item) next.splice(index + by, 0, item)
    onChange(next)
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="hidden grid-cols-[minmax(0,1fr)_5.5rem_8rem_7rem_2rem] gap-3 px-3 sm:grid">
        <span className="label">Items</span>
        <span className="label">Qty</span>
        <span className="label">Price</span>
        <span className="label text-right">Amount</span>
        <span />
      </div>

      {lines.length === 0 ? (
        <p className="rounded-md bg-paper px-3 py-6 text-center text-[length:var(--text-sm)] text-taupe">
          {readOnly ? 'No items.' : 'No items yet. Add one below.'}
        </p>
      ) : null}

      {lines.map((line, index) =>
        readOnly ? (
          <div key={line.key} className="grid gap-x-3 gap-y-1 rounded-md bg-paper p-3 sm:grid-cols-[minmax(0,1fr)_5.5rem_8rem_7rem_2rem]">
            <div className="min-w-0">
              <p className="text-[length:var(--text-sm)]">{line.description}</p>
              {line.detail ? <p className="text-[length:var(--text-2xs)] text-taupe">{line.detail}</p> : null}
            </div>
            <span className="text-[length:var(--text-sm)]" data-numeric>
              {line.quantity}
            </span>
            <span className="text-[length:var(--text-sm)]" data-numeric>
              {formatMoney(line.unitPrice)}
            </span>
            <span className="text-right text-[length:var(--text-sm)]" data-numeric>
              {formatMoney(lineAmount(line.quantity, line.unitPrice))}
            </span>
          </div>
        ) : (
          <div key={line.key} className="grid gap-3 rounded-md bg-paper p-3 sm:grid-cols-[minmax(0,1fr)_5.5rem_8rem_7rem_2rem] sm:items-start">
            <div className="flex flex-col gap-1.5">
              <Input aria-label="Item name" placeholder="Item name" value={line.description} onChange={(e) => set(line.key, { description: e.target.value })} />
              <Input
                aria-label="Item description"
                placeholder="Item description"
                className="text-[length:var(--text-2xs)]"
                value={line.detail}
                onChange={(e) => set(line.key, { detail: e.target.value })}
              />
            </div>
            <NumberBox label="Qty" value={line.quantity} onChange={(quantity) => set(line.key, { quantity })} />
            <NumberBox label="Price" prefix="R" value={line.unitPrice} onChange={(unitPrice) => set(line.key, { unitPrice })} allowNegative />
            <span className="self-center text-right text-[length:var(--text-sm)] sm:pt-0" data-numeric>
              {formatMoney(lineAmount(line.quantity, line.unitPrice))}
            </span>
            <div className="flex gap-1 sm:flex-col">
              <button type="button" aria-label="Remove this item" className="px-1 text-taupe hover:text-clay" onClick={() => onChange(lines.filter((item) => item.key !== line.key))}>
                ×
              </button>
              {index > 0 ? (
                <button type="button" aria-label="Move up" className="px-1 text-taupe hover:text-ink" onClick={() => move(index, -1)}>
                  ↑
                </button>
              ) : null}
            </div>
          </div>
        ),
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        {!readOnly ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="ghost" className="text-accent" onClick={() => onChange([...lines, blankLine()])}>
              ⊕ Add item
            </Button>
            <Button size="sm" variant="ghost" className="text-accent" onClick={() => onChange([...lines, blankLine({ description: 'Discount', unitPrice: 0, source: 'discount' })])}>
              Add discount
            </Button>
            {travel ? (
              <Button
                size="sm"
                variant="ghost"
                className="text-accent"
                onClick={() =>
                  onChange([
                    ...lines,
                    blankLine({
                      description: 'Travel',
                      detail: travel.km ? 'Return trip, per km' : 'Per km',
                      quantity: travel.km ?? 1,
                      unitPrice: travel.ratePerKm,
                      source: 'travel',
                    }),
                  ])
                }
              >
                Add travel
              </Button>
            ) : null}
            {quickAdds.length ? (
              <Select
                aria-label="Add from your collections and add-ons"
                className="h-7 w-auto text-[length:var(--text-xs)]"
                value=""
                onChange={(e) => {
                  const pick = quickAdds.find((item) => item.id === e.target.value)
                  if (pick)
                    onChange([
                      ...lines,
                      blankLine({
                        description: pick.label,
                        detail: pick.detail ?? '',
                        unitPrice: pick.unitPrice,
                        source: pick.source,
                        sourceId: pick.id,
                      }),
                    ])
                }}
              >
                <option value="">Add a collection or add-on…</option>
                {quickAdds.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label} · {formatMoney(item.unitPrice)}
                  </option>
                ))}
              </Select>
            ) : null}
          </div>
        ) : (
          <span />
        )}

        <dl className="grid min-w-[16rem] grid-cols-[1fr_auto] gap-x-6 gap-y-2 text-[length:var(--text-sm)]">
          <dt className="text-taupe">Subtotal</dt>
          <dd className="text-right" data-numeric>
            {formatMoney(totals.subtotal)}
          </dd>
          {vatRate ? (
            <>
              <dt className="text-taupe">VAT ({Math.round(vatRate * 100)}%)</dt>
              <dd className="text-right" data-numeric>
                {formatMoney(totals.vat)}
              </dd>
            </>
          ) : null}
          <dt className="border-t pt-2 font-semibold">Total</dt>
          <dd className="border-t pt-2 text-right font-semibold" data-numeric>
            {formatMoney(totals.total)}
          </dd>
        </dl>
      </div>
    </div>
  )
}

/** A number typed freely ("7.", "1 290"), passed up as a number once it parses. */
function NumberBox({
  label,
  value,
  onChange,
  prefix,
  allowNegative = false,
}: {
  label: string
  value: number
  onChange: (value: number) => void
  prefix?: string
  allowNegative?: boolean
}) {
  const [text, setText] = useState(String(value))
  const [focused, setFocused] = useState(false)
  useEffect(() => {
    if (!focused) setText(String(value))
  }, [value, focused])

  return (
    <label className={cn('flex h-[var(--size-control)] items-center gap-1 rounded-md border border-[var(--color-rule-strong)] bg-[var(--surface-card)] px-2')}>
      <span className="sr-only">{label}</span>
      {prefix ? <span className="text-[length:var(--text-sm)] text-taupe">{prefix}</span> : null}
      <input
        inputMode="decimal"
        className="w-full min-w-0 bg-transparent text-[length:var(--text-sm)] outline-none"
        value={text}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          setText(e.target.value)
          const parsed = Number(e.target.value.replace(/[\s,]/g, ''))
          if (Number.isFinite(parsed) && (allowNegative || parsed >= 0)) onChange(parsed)
        }}
      />
    </label>
  )
}
